import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';

import {
  TypedConfirmationDialogComponent,
} from './ui/typed-confirmation-dialog/typed-confirmation-dialog.component';

export interface DeleteAllDataOptions {
  /**
   * What one row is called, singular and lower case: "lead", "customer". It
   * is the only thing that differs between the tables that offer this, so it
   * is the only thing asked for - the title, the warning, the snackbar and
   * the failure message are all built from it.
   */
  readonly noun: string;
  /** The request itself. The page's own API service still makes it. */
  run: () => Observable<{ deletedCount: number }>;
  /** Read the page again: the rows it is showing are gone. */
  reload: () => void;
}

/**
 * "Delete all X": the typed confirmation, the request, and what is said after.
 *
 * Shared because this is the most dangerous button on any of these pages and
 * the parts that make it safe - that the word DELETE must be typed, that the
 * dialog cannot be dismissed by clicking away, that nothing is sent unless
 * the answer was exactly `true` - are the parts most easily lost when a page
 * copies it. One copy means a table added next year cannot get a weaker
 * version of this by accident.
 *
 * Returns a cold observable so the caller can put `takeUntil` and a `finalize`
 * that releases its own in-flight flag on it before subscribing. It completes
 * whether the dialog was dismissed, the delete succeeded or it failed, and it
 * never errors - so `.subscribe()` with no handlers is correct and the flag is
 * always released.
 */
export function deleteAllData(
  dialog: MatDialog,
  snackBar: MatSnackBar | undefined,
  options: DeleteAllDataOptions,
): Observable<unknown> {
  const { noun } = options;
  return dialog.open(TypedConfirmationDialogComponent, {
    data: {
      title: `Delete all ${noun} data`,
      message:
        `This permanently deletes all ${noun} data in this account. `
        + 'This cannot be undone.',
      confirmationText: 'DELETE',
      confirmLabel: 'Delete All',
    },
    width: '480px',
    maxWidth: 'calc(100vw - 32px)',
    disableClose: true,
  }).afterClosed().pipe(
    // Exactly true, not merely truthy: a dialog closed by any other route
    // hands back undefined, and this is not a button to be generous about.
    filter(confirmed => confirmed === true),
    switchMap(() => options.run()),
    tap(result => {
      snackBar?.open(
        `Deleted ${result.deletedCount} ${noun}`
        + `${result.deletedCount === 1 ? '' : 's'}.`,
        undefined,
        { duration: 3000 });
      options.reload();
    }),
    catchError(error => {
      snackBar?.open(
        error?.error?.error ?? `Could not delete all ${noun} data.`,
        'OK',
        { duration: 5000 });
      return EMPTY;
    }),
  );
}
